import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../Icon/Icon";
import InputDatePicker from "../Inputs/InputDatePicker";
import "./CommsList.scss";

interface ICommsListProps {
  arrData: any[];
}

const PAGE_SIZE = 5;

type TabId = "todos" | "recibidas" | "enviadas";

const TABS: { id: TabId; label: string }[] = [
  { id: "todos", label: "Todas" },
  { id: "recibidas", label: "Recibidas" },
  { id: "enviadas", label: "Enviadas" },
];

const formatDateShort = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const CommsList: React.FC<ICommsListProps> = ({ arrData }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("todos");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [onlyAttached, setOnlyAttached] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);

  const handleTabChange = (id: TabId) => {
    setActiveTab(id);
    setPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);
  };

  const enriched = (arrData || []).map((item: any) => {
    const kind = item.puedeResponder
      ? "warn"
      : item.comunicacionReferenciaId
      ? "success"
      : "info";
    const icon = item.puedeResponder
      ? "alert"
      : item.comunicacionReferenciaId
      ? "check"
      : "chat";
    const sender = item.autorUsuario ? "Usted" : item.nombreAutor || "";
    return { ...item, kind, icon, sender };
  });

  const q = search.trim().toLowerCase();

  const matchesDate = (item: any): boolean => {
    if (!dateFilter) return true;
    if (!item.created) return false;
    const d = new Date(item.created);
    const [dd, mm, yyyy] = dateFilter.split('/');
    return (
      d.getFullYear() === parseInt(yyyy) &&
      d.getMonth() === parseInt(mm) - 1 &&
      d.getDate() === parseInt(dd)
    );
  };

  const applyCommonFilters = (items: any[]) =>
    items.filter((item) => {
      if (onlyAttached && !(item.files?.length > 0)) return false;
      if (!matchesDate(item)) return false;
      if (!q) return true;
      return (
        item.asunto?.toLowerCase().includes(q) ||
        item.sender?.toLowerCase().includes(q)
      );
    });

  const isPendiente = (item: any) => item.puedeResponder;
  const isRespondida = (item: any) => {
    const hasReplies =
      (Array.isArray(item.comunicaciones) && item.comunicaciones.length > 0) ||
      (Array.isArray(item.respuestas) && item.respuestas.length > 0);
    return !!item.comunicacionReferenciaId || !!item.respondida || hasReplies;
  };
  const isEnviada = (item: any) => item.autorUsuario && !item.comunicacionReferenciaId;
  const isRecibida = (item: any) => !item.autorUsuario && !item.comunicacionReferenciaId && !item.puedeResponder;

  const counts: Record<TabId, number> = {
    todos: applyCommonFilters(enriched).length,
    recibidas: applyCommonFilters(enriched.filter((item) => isPendiente(item) || isRecibida(item))).length,
    enviadas: applyCommonFilters(enriched.filter((item) => isRespondida(item) || isEnviada(item))).length,
  };

  const tabFiltered = enriched.filter((item) => {
    if (activeTab === "recibidas") return isPendiente(item) || isRecibida(item);
    if (activeTab === "enviadas") return isRespondida(item) || isEnviada(item);
    return true;
  });

  const filtered = applyCommonFilters(tabFiltered);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="comms-list">
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            {counts[tab.id] > 0 && (
              <span className={`tabs__count${tab.id === "recibidas" && counts.recibidas > 0 ? " tabs__count--warn" : ""}`}>
                {counts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="comms-filters">
        <div className="comms-search">
        <Icon name="search" size={16} />
        <input
          className="comms-search__input"
          type="text"
          placeholder="Buscar por asunto o remitente…"
          value={search}
          onChange={handleSearch}
          autoComplete="off"
          spellCheck={false}
        />
        {search && (
          <button className="comms-search__clear" onClick={handleClearSearch} aria-label="Limpiar búsqueda">
            <Icon name="x" size={14} />
          </button>
        )}
        </div>
        <button
          className={`comms-filter-chip${onlyAttached ? " is-active" : ""}`}
          onClick={() => { setOnlyAttached((v) => !v); setPage(1); }}
        >
          <Icon name="paperclip" size={14} />
          Con adjuntos
        </button>
        <div className="comms-date-filter">
          <InputDatePicker
            id="comms-date"
            name="comms-date"
            placeholder="Filtrar por fecha"
            value={dateFilter}
            showClearButton={false}
            onChange={(val) => { setDateFilter(val); setPage(1); }}
          />
          {dateFilter && (
            <button
              className="comms-search__clear comms-date-filter__clear"
              onClick={() => { setDateFilter(null); setPage(1); }}
              aria-label="Limpiar fecha"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="comms-list__empty">
          {q ? `Sin resultados para "${search}".` : "No hay comunicaciones para mostrar."}
        </p>
      ) : (
        <>
          <div className="comms">
            {paged.map((n: any, i: number) => {
              const showPendingReplyTip = activeTab === "recibidas" && isPendiente(n);
              const showRespondedTip = activeTab === "enviadas" && isRespondida(n);

              return (
                <article
                  key={n.id || i}
                  className={`comm comm--${n.kind}`}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/comunicaciones/${n.id}/comunicacion-general`, {
                      state: { subject: "Detalle", communicationId: n.id },
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/comunicaciones/${n.id}/comunicacion-general`, {
                        state: { subject: "Detalle", communicationId: n.id },
                      });
                    }
                  }}
                >
                  <div className="comm__icon">
                    <Icon name={n.icon} size={22} />
                  </div>
                  <div className="comm__content">
                    <div className="comm__title">
                      {n.puedeResponder && (
                        <span className="comm__unread" aria-label="No leído" />
                      )}
                      {n.asunto}
                      {showPendingReplyTip && (
                        <span
                          className="comm__status-tip comm__status-tip--pending"
                          aria-label="Puede responder"
                          tabIndex={0}
                        >
                          <Icon name="alert" size={12} />
                          <span className="comm__status-tooltip" role="tooltip">
                            Puede responder
                          </span>
                        </span>
                      )}
                      {showRespondedTip && (
                        <span
                          className="comm__status-tip comm__status-tip--responded"
                          aria-label="Respondida"
                          tabIndex={0}
                        >
                          <Icon name="check" size={12} />
                          <span className="comm__status-tooltip" role="tooltip">
                            Respondida
                          </span>
                        </span>
                      )}
                    </div>
                    {n.sender && <div className="comm__sender">{n.sender}</div>}
                    <div className="comm__body">{n.mensaje}</div>
                  </div>
                  <div className="comm__date">
                    {n.files?.length > 0 && (
                      <span className="comm__attach-badge" title="Tiene adjuntos">
                        <Icon name="paperclip" size={13} />
                      </span>
                    )}
                    <span className="comm__date-clip">
                      <Icon name="clock" size={11} />
                      <span className="comm__date-clip-num">
                        {n.created ? formatDateShort(n.created) : "—"}
                      </span>
                    </span>
                  </div>
                </article>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="comms-list__pager">
              <span className="comms-list__pager-info">
                {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
              </span>
              <div className="comms-list__pager-btns">
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
                >
                  <Icon name="arrow-left" size={16} /> Anterior
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
                >
                  Siguiente <Icon name="arrow-right" size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommsList;
